/**
 * Branding Tag Detector – public API.
 *
 * Usage:
 *   import { detectBrandingTags } from '@/shared/services/brandingTagDetector'
 *   const result = await detectBrandingTags(file)
 *   // result.state: 'detected' | 'uncertain' | 'not_detected'
 */

import { classifyResult } from './brandingMarkers.js'

// Extensions that can be inspected as ZIP+XML
const MODERN_WORD_EXTENSIONS = new Set(['docx', 'dotx', 'docm', 'dotm'])
const MODERN_EXCEL_EXTENSIONS = new Set(['xlsx', 'xlsm', 'xltx', 'xltm'])
const MODERN_PPT_EXTENSIONS = new Set(['pptx', 'pptm', 'ppsx'])
const ODF_PRESENTATION_EXTENSIONS = new Set(['odp'])
const ODF_SPREADSHEET_EXTENSIONS = new Set(['ods'])

// Legacy binary formats → uncertain
const LEGACY_EXTENSIONS = new Set(['doc', 'xls', 'ppt'])

const DEFAULT_OPTIONS = {
  timeoutMs: 5000,
  maxXmlBytes: 2 * 1024 * 1024,
  maxMarkers: 20
}

/**
 * Extract file extension (lowercase, no dot).
 */
function getExtension(file) {
  const name = file?.name || ''
  const parts = name.split('.')
  return parts.length > 1 ? parts.pop().toLowerCase() : ''
}

/**
 * Detect whether a File contains branding template tags.
 *
 * @param {File} file
 * @param {object} [options]
 * @param {number} [options.timeoutMs=5000]
 * @param {number} [options.maxXmlBytes=2097152]
 * @param {number} [options.maxMarkers=20]
 * @returns {Promise<{ state: 'detected'|'uncertain'|'not_detected', markers: Array, reason: string }>}
 */
export async function detectBrandingTags(file, options = {}) {
  if (!file) {
    return classifyResult({ fullyInspected: false, reason: 'No file provided.' })
  }

  const mergedOptions = { ...DEFAULT_OPTIONS, ...options }
  const ext = getExtension(file)

  // Legacy binary formats cannot be inspected client-side
  if (LEGACY_EXTENSIONS.has(ext)) {
    return classifyResult({
      fullyInspected: false,
      reason: `Legacy Office format (.${ext}) cannot be inspected client-side.`
    })
  }

  // Modern Word formats
  if (MODERN_WORD_EXTENSIONS.has(ext)) {
    const { detectWord } = await import('./wordDetector.js')
    return detectWord(file, mergedOptions)
  }

  // Modern Excel formats
  if (MODERN_EXCEL_EXTENSIONS.has(ext)) {
    const { detectExcel } = await import('./excelDetector.js')
    return detectExcel(file, mergedOptions)
  }

  // Modern PowerPoint formats
  if (MODERN_PPT_EXTENSIONS.has(ext)) {
    const { detectPowerPoint } = await import('./pptDetector.js')
    return detectPowerPoint(file, mergedOptions)
  }

  // OpenDocument presentation formats
  if (ODF_PRESENTATION_EXTENSIONS.has(ext)) {
    const { detectOdpPresentation } = await import('./odpDetector.js')
    return detectOdpPresentation(file, mergedOptions)
  }

  // OpenDocument spreadsheet formats
  if (ODF_SPREADSHEET_EXTENSIONS.has(ext)) {
    const { detectOdsSpreadsheet } = await import('./odsDetector.js')
    return detectOdsSpreadsheet(file, mergedOptions)
  }

  // Not a Word, Excel, or PowerPoint file → not applicable for detection
  return classifyResult({
    markers: [],
    fullyInspected: true,
    reason: 'File type is not a Word, Excel, or PowerPoint document.'
  })
}

/**
 * Check if a file is eligible for branding tag detection based on extension.
 * Use this to decide whether to run detection at all.
 *
 * @param {string} fileName
 * @returns {boolean}
 */
export function isBrandingTagDetectable(fileName) {
  if (!fileName) return false
  const parts = fileName.split('.')
  const ext = parts.length > 1 ? parts.pop().toLowerCase() : ''
  return MODERN_WORD_EXTENSIONS.has(ext) || MODERN_EXCEL_EXTENSIONS.has(ext) || MODERN_PPT_EXTENSIONS.has(ext) || ODF_PRESENTATION_EXTENSIONS.has(ext) || ODF_SPREADSHEET_EXTENSIONS.has(ext) || LEGACY_EXTENSIONS.has(ext)
}
